import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, ArrowLeft, Loader2, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { AuthLayout } from '../components/AuthLayout';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';

const ResetPassword = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.resetPassword.passwordMatchError'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.resetPassword.passwordLengthError'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        password: formData.password
      });
      showToast(t('auth.resetPassword.successMessage'));
      navigate('/login');
    } catch (err) {
      setError(err.response?.data || t('auth.resetPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout
        title={t('auth.resetPassword.invalidLinkTitle')}
        subtitle={t('auth.resetPassword.invalidLinkSubtitle')}
      >
        <div className="bg-red-50 border-2 border-red-100 rounded-3xl p-8 text-center animate-scaleIn">
          <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center text-white mx-auto mb-6 shadow-lg shadow-red-500/20">
            <Lock className="w-10 h-10" />
          </div>
          <p className="text-slate-600 mb-8 font-medium">
            {t('auth.resetPassword.invalidLinkSubtitle')}
          </p>
          <Button onClick={() => navigate('/forgot-password')} className="w-full">
            {t('auth.resetPassword.requestNewLink')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.resetPassword.title')}
      subtitle={t('auth.resetPassword.subtitle')}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm font-medium animate-shake">
            {error}
          </div>
        )}

        <Input
          label={t('auth.resetPassword.newPasswordLabel')}
          id="password"
          name="password"
          type="password"
          required
          value={formData.password}
          onChange={handleChange}
          placeholder={t('auth.resetPassword.passwordPlaceholder')}
          icon={<Lock className="w-5 h-5" />}
          minLength="6"
        />

        <Input
          label={t('auth.resetPassword.confirmPasswordLabel')}
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          required
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder={t('auth.resetPassword.passwordPlaceholder')}
          icon={<Lock className="w-5 h-5" />}
          minLength="6"
        />

        <Button
          type="submit"
          disabled={loading}
          className="w-full"
          size="lg"
          rightIcon={!loading && <Save className="w-5 h-5" />}
        >
          {loading ? t('auth.resetPassword.resettingButton') : t('auth.resetPassword.resetButton')}
        </Button>

        <Link
          to="/login"
          className="flex items-center justify-center text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
